import { useState, useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 1099 INDEPENDENT CONTRACTOR AGREEMENT MODAL
 *
 * Court-defensible gate: blocks all Crew Portal access until the worker has
 * read, scrolled to the bottom of, and electronically signed this agreement.
 * Acceptance is logged with timestamp + IP + user-agent server-side.
 */
const ICAgreementGate = ({ onAccepted }) => {
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [agreementText, setAgreementText] = useState("");
  const [version, setVersion] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/crew/agreement`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          if (cancel) return;
          setAgreementText(d.text || "");
          setVersion(d.version || "");
          setAccepted(!!d.accepted);
          if (d.accepted && typeof onAccepted === "function") onAccepted();
        }
      } catch {}
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [onAccepted]);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true);
  };

  const submit = async () => {
    setErrorMsg("");
    if (!scrolledToEnd) {
      setErrorMsg("Please scroll through the entire agreement before signing.");
      return;
    }
    if (signatureName.trim().length < 2) {
      setErrorMsg("Please type your full legal name to sign.");
      return;
    }
    if (!confirmed) {
      setErrorMsg("Please check the confirmation box.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/crew/agreement/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_name: signatureName.trim(), confirmed: true })
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setAccepted(true);
        if (typeof onAccepted === "function") onAccepted();
      } else {
        setErrorMsg(d.detail || "Could not record acceptance. Try again.");
      }
    } catch {
      setErrorMsg("Network error. Try again.");
    }
    setSubmitting(false);
  };

  if (loading) return null;
  if (accepted) return null;

  return (
    <div data-testid="ic-agreement-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(8px)", zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "2rem 1rem", overflowY: "auto" }}>
      <div data-testid="ic-agreement-modal" style={{ background: "#0a1929", border: "2px solid #FFB347", borderRadius: 12, maxWidth: 760, width: "100%", padding: "1.75rem 2rem", boxShadow: "0 20px 60px rgba(0,0,0,0.7)" }}>
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ display: "inline-block", padding: "0.25rem 0.7rem", borderRadius: 4, fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.75rem", background: "rgba(255,179,71,0.18)", color: "#FFB347", border: "1px solid rgba(255,179,71,0.40)" }}>
            ⚠ Required Before Crew Portal Access
          </div>
          <h2 style={{ color: "#fff", fontSize: "1.4rem", fontWeight: 800, margin: "0 0 0.3rem" }}>1099 Independent Contractor Agreement</h2>
          <p style={{ color: "#ADBED1", fontSize: "0.86rem", margin: 0, lineHeight: 1.5 }}>
            Read carefully. This is a binding legal agreement between you and Moovin Forward LLC. You must read all terms, type your full legal name, and confirm acceptance below.
          </p>
        </div>

        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          data-testid="agreement-scroll"
          style={{ background: "#fff", color: "#1a1a1a", padding: "1.25rem 1.5rem", borderRadius: 6, maxHeight: 360, overflowY: "auto", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: "Georgia, 'Times New Roman', serif", marginBottom: "0.85rem" }}
        >
          {agreementText}
        </div>

        {!scrolledToEnd && (
          <p data-testid="scroll-hint" style={{ color: "#FFB347", fontSize: "0.78rem", margin: "0 0 0.85rem", fontStyle: "italic" }}>
            ↓ Scroll the agreement to the bottom to enable the signature field.
          </p>
        )}

        <div style={{ marginBottom: "0.85rem" }}>
          <label style={{ display: "block", color: "#ADBED1", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.3rem" }}>Type your full legal name (your electronic signature)</label>
          <input
            type="text"
            value={signatureName}
            onChange={e => setSignatureName(e.target.value)}
            disabled={!scrolledToEnd}
            data-testid="ic-signature-input"
            placeholder="e.g., Marcus J. Williams"
            style={{ width: "100%", background: scrolledToEnd ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)", border: `1px solid ${scrolledToEnd ? "rgba(0,255,255,0.30)" : "rgba(255,255,255,0.10)"}`, color: "#fff", padding: "0.7rem 0.9rem", borderRadius: 6, fontSize: "1rem", fontFamily: "Georgia, serif", fontStyle: "italic", boxSizing: "border-box" }}
          />
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", marginBottom: "0.85rem", cursor: scrolledToEnd ? "pointer" : "not-allowed", opacity: scrolledToEnd ? 1 : 0.5 }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            disabled={!scrolledToEnd}
            data-testid="ic-confirm-checkbox"
            style={{ accentColor: "#00DC78", width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
          />
          <span style={{ color: "#fff", fontSize: "0.84rem", lineHeight: 1.5 }}>
            I have read this Independent Contractor Agreement in its entirety, I understand and agree to all terms, my typed name above is my legal binding signature, and I authorize Moovin Forward to record my IP address, device, and timestamp as evidence of my electronic execution of this Agreement.
          </span>
        </label>

        {errorMsg && (
          <p data-testid="ic-error" style={{ color: "#FF8B6B", fontSize: "0.82rem", margin: "0 0 0.6rem" }}>✗ {errorMsg}</p>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
          <span style={{ color: "#7E92A8", fontSize: "0.7rem" }}>
            Version: {version} &nbsp;·&nbsp; Governing law: Texas
          </span>
          <button
            onClick={submit}
            disabled={!scrolledToEnd || !signatureName.trim() || !confirmed || submitting}
            data-testid="ic-accept-btn"
            style={{ background: scrolledToEnd && signatureName.trim() && confirmed && !submitting ? "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)" : "rgba(255,255,255,0.06)", color: scrolledToEnd && signatureName.trim() && confirmed && !submitting ? "#030B1A" : "#7E92A8", border: "none", padding: "0.85rem 1.5rem", borderRadius: 8, fontWeight: 800, fontSize: "0.88rem", cursor: scrolledToEnd && signatureName.trim() && confirmed && !submitting ? "pointer" : "not-allowed" }}
          >
            {submitting ? "Recording…" : "✓ I Accept & Sign"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ICAgreementGate;
